import { ChangeDetectionStrategy, Component, inject, input, model, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'ld-star-rating',
  imports: [MatIconModule, TranslocoDirective],
  templateUrl: './star-rating.component.html',
  styleUrl: './star-rating.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StarRatingComponent {
  private readonly translocoService = inject(TranslocoService);

  value = model<number>(0);

  readonly = input<boolean>(false);

  size = input<'small' | 'medium' | 'large'>('medium');

  protected readonly stars = [1, 2, 3, 4, 5];

  protected readonly hoveredStar = signal(0);

  protected onStarClick(star: number): void {
    if (this.readonly()) return;
    this.value.set(star);
  }

  protected onStarHover(star: number): void {
    if (this.readonly()) return;
    this.hoveredStar.set(star);
  }

  protected onStarLeave(): void {
    this.hoveredStar.set(0);
  }

  protected isStarFilled(star: number): boolean {
    const hovered = this.hoveredStar();
    if (hovered > 0) {
      return star <= hovered;
    }
    return star <= this.value();
  }

  protected getStarIcon(star: number): string {
    return this.isStarFilled(star) ? 'star' : 'star_border';
  }

  protected getStarAriaLabel(star: number): string {
    return this.translocoService.translate('commonAriaRateStars', { star });
  }

  protected getSizeClass(): string {
    switch (this.size()) {
      case 'small':
        return 'text-xl';
      case 'large':
        return 'text-5xl';
      default:
        return 'text-3xl';
    }
  }
}
